import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Label, StringSelect, Checkbox, RichTextEditor } from "@tellescope/ui";
import type { ImageUploadHandler } from "@tellescope/ui";
import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import type { BannerBlock } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import type { BlockTypeEditorProps } from "../types";
import { useAuth } from "@tellescope/tanstack-query";

type BannerBlockEditorProps = BlockTypeEditorProps<BannerBlock>;

export function BannerBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: BannerBlockEditorProps) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { uploadFileAsync } = useUploadTellescopeFile({
    session,
    returnDownloadURL: true,
  });

  const uploadImage: ImageUploadHandler = useCallback(
    async (file: File) => {
      if (!session) {
        console.error("No active session for image upload");
        return null;
      }

      try {
        const result = await uploadFileAsync(
          {
            name: file.name,
            size: file.size,
            type: file.type,
            publicRead: true,
          },
          file
        );

        if (result.downloadURL) {
          return { url: result.downloadURL };
        }

        return null;
      } catch (error) {
        console.error("Failed to upload image:", error);
        return null;
      }
    },
    [session, uploadFileAsync]
  );

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Banner Content")}</Label>
        <RichTextEditor
          content={block.props.content}
          onChange={(content) => updateProps({ content })}
          placeholder={t("Enter banner content...")}
          onImageUpload={uploadImage}
        />
      </div>
      <StringSelect
        label={t("Variant")}
        value={block.props.variant}
        onValueChange={(value) => updateProps({ variant: value })}
        options={[
          { value: "info", label: t("Info") },
          { value: "success", label: t("Success") },
          { value: "warning", label: t("Warning") },
          { value: "error", label: t("Error") },
          { value: "neutral", label: t("Neutral") },
        ]}
      />
      <div className="flex items-center gap-2">
        <Checkbox
          id="dismissible"
          checked={block.props.dismissible ?? false}
          onCheckedChange={(checked) => updateProps({ dismissible: !!checked })}
        />
        <label htmlFor="dismissible" className="text-sm cursor-pointer">
          {t("Dismissible")}
        </label>
      </div>
      <p className="text-xs text-muted-foreground">{t("Allow users to dismiss this banner")}</p>
      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
